import React from 'react';
import { Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@/modules/auth/ProtectedRoute';

// Lazy-loaded tab containers
const GestionDocentePage = React.lazy(() => import('@/modules/academic/teachers/GestionDocentePage'));
const MisProgramasPage = React.lazy(() => import('@/modules/coordination/programs/MisProgramasPage'));
const SeguimientoAlumnosPage = React.lazy(() => import('@/modules/coordination/tracking/SeguimientoAlumnosPage'));
const VinculacionPage = React.lazy(() => import('@/modules/coordination/outreach/VinculacionPage'));
const ContenidosPage = React.lazy(() => import('@/modules/coordination/content/ContenidosPage'));
const MateriaDashboard = React.lazy(() => import('@/modules/coordination/content/materia/MateriaDashboard'));
const ProgramTemarioView = React.lazy(() => import('@/modules/coordination/programs/components/ProgramTemarioView'));

// Detail routes (outside tabs)
const TeacherDetailPage = React.lazy(() => import('@/modules/academic/teachers/TeacherDetailPage'));
const TeacherEditPage = React.lazy(() => import('@/modules/academic/teachers/TeacherEditPage'));

export const coordinationRoutes = [
  // Gestión Docente (2 niveles + tabs)
  <Route key="/coordinacion-carreras/docentes" path="/coordinacion-carreras/docentes" element={
    <Navigate to="/coordinacion-carreras/docentes/listado" replace />
  } />,
  <Route key="/coordinacion-carreras/docentes/listado" path="/coordinacion-carreras/docentes/listado" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><GestionDocentePage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/docentes/carga-academica" path="/coordinacion-carreras/docentes/carga-academica" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><GestionDocentePage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/docentes/horarios" path="/coordinacion-carreras/docentes/horarios" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><GestionDocentePage /></ProtectedRoute>
  } />,
  // Detail routes — viven fuera del tab container
  <Route key="/coordinacion-carreras/docentes/:id/editar" path="/coordinacion-carreras/docentes/:id/editar" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><TeacherEditPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/docentes/:id" path="/coordinacion-carreras/docentes/:id" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><TeacherDetailPage /></ProtectedRoute>
  } />,

  // Mis Programas (2 niveles + tabs)
  <Route key="/coordinacion-carreras/programas" path="/coordinacion-carreras/programas" element={
    <Navigate to="/coordinacion-carreras/programas/planificacion" replace />
  } />,
  <Route key="/coordinacion-carreras/programas/planificacion" path="/coordinacion-carreras/programas/planificacion" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><MisProgramasPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/programas/matriculas" path="/coordinacion-carreras/programas/matriculas" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><MisProgramasPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/programas/avance-curricular" path="/coordinacion-carreras/programas/avance-curricular" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><MisProgramasPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/programas/mapa-riesgo" path="/coordinacion-carreras/programas/mapa-riesgo" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><MisProgramasPage /></ProtectedRoute>
  } />,
  // Detail route: temario a nivel PLAN (fuera del tab container)
  <Route key="/coordinacion-carreras/programas/:planId/temario" path="/coordinacion-carreras/programas/:planId/temario" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><ProgramTemarioView /></ProtectedRoute>
  } />,

  // Seguimiento a Alumnos (2 niveles + tabs)
  <Route key="/coordinacion-carreras/seguimiento" path="/coordinacion-carreras/seguimiento" element={
    <Navigate to="/coordinacion-carreras/seguimiento/alertas-tempranas" replace />
  } />,
  <Route key="/coordinacion-carreras/seguimiento/alertas-tempranas" path="/coordinacion-carreras/seguimiento/alertas-tempranas" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><SeguimientoAlumnosPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/seguimiento/tutorias-asesorias" path="/coordinacion-carreras/seguimiento/tutorias-asesorias" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><SeguimientoAlumnosPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/seguimiento/servicio-social-practicas" path="/coordinacion-carreras/seguimiento/servicio-social-practicas" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><SeguimientoAlumnosPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/seguimiento/trayectoria-academica" path="/coordinacion-carreras/seguimiento/trayectoria-academica" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><SeguimientoAlumnosPage /></ProtectedRoute>
  } />,

  // Vinculación (2 niveles + tabs)
  <Route key="/coordinacion-carreras/vinculacion" path="/coordinacion-carreras/vinculacion" element={
    <Navigate to="/coordinacion-carreras/vinculacion/empresas-practicas" replace />
  } />,
  <Route key="/coordinacion-carreras/vinculacion/empresas-practicas" path="/coordinacion-carreras/vinculacion/empresas-practicas" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><VinculacionPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/vinculacion/bolsa-trabajo" path="/coordinacion-carreras/vinculacion/bolsa-trabajo" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><VinculacionPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/vinculacion/seguimiento-egresados" path="/coordinacion-carreras/vinculacion/seguimiento-egresados" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><VinculacionPage /></ProtectedRoute>
  } />,
  <Route key="/coordinacion-carreras/vinculacion/eventos-academicos" path="/coordinacion-carreras/vinculacion/eventos-academicos" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><VinculacionPage /></ProtectedRoute>
  } />,

  // Contenidos — listado de materias
  <Route key="/coordinacion-carreras/contenidos" path="/coordinacion-carreras/contenidos" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><ContenidosPage /></ProtectedRoute>
  } />,
  // Contenidos — Dashboard de materia
  <Route key="/coordinacion-carreras/contenidos/materia/:materiaId" path="/coordinacion-carreras/contenidos/materia/:materiaId" element={
    <ProtectedRoute requiredPermission="careers.teachers.view"><MateriaDashboard /></ProtectedRoute>
  } />,
];
